import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  generated,
  signatures,
  type GeneratedDocument,
  type GeneratedKind,
  type SignatureFlow,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { AcaoComIcone, LinkComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { KpiCard } from '@/shared/ui/KpiCard';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { classeDaAba, trilhoDeAbas } from '@/shared/ui/tabStyles';

import { BaixarDocumento, SignatureFlowBlock } from './GenerationPanel';
import {
  PERMISSAO_CONFIGURAR_ASSINATURA,
  PERMISSAO_VER_PEDIDOS,
} from './signatureRequestLabels';
import { SignatureRequestsTab } from './SignatureRequestsTab';
import { SignatureSettingsTab } from './SignatureSettingsTab';

/**
 * Filas de minuta, CCG e assinatura.
 *
 * A geracao acontece na operacao, onde o texto ganha sentido. Aqui a pergunta e
 * sobre o conjunto: o que ja foi gerado, e o que ainda espera assinatura.
 */

const limite = 25;

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'substituida', label: 'Substituida' },
  { value: 'cancelada', label: 'Cancelada' },
];

const rotuloDaSituacao: Record<
  string,
  { label: string; tone: 'success' | 'neutral' | 'danger' }
> = {
  vigente: { label: 'Vigente', tone: 'success' },
  substituida: { label: 'Substituida', tone: 'neutral' },
  cancelada: { label: 'Cancelada', tone: 'danger' },
};

const situacaoDaColeta: Record<
  string,
  { label: string; tone: 'info' | 'success' | 'neutral' }
> = {
  em_coleta: { label: 'Em coleta', tone: 'info' },
  concluido: { label: 'Concluido', tone: 'success' },
  cancelado: { label: 'Cancelado', tone: 'neutral' },
};

function GeneratedListPage({
  kind,
  titulo,
  descricao,
}: {
  kind: GeneratedKind;
  titulo: string;
  descricao: string;
}) {
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [vendo, setVendo] = useState<GeneratedDocument | null>(null);

  const consulta = useQuery({
    queryKey: ['generated', kind, { status, offset }],
    queryFn: ({ signal }) => {
      const params = { status, limit: limite, offset };
      return kind === 'ccg'
        ? generated.listCounterGuarantees(params, signal)
        : generated.listDrafts(params, signal);
    },
  });

  const colunas: Column<GeneratedDocument>[] = [
    {
      key: 'numero',
      header: titulo,
      render: (documento) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{documento.number}</p>
          <p className="truncate text-2xs text-ink-500">
            Template versao {documento.template_version}
          </p>
        </div>
      ),
    },
    {
      key: 'autor',
      header: 'Gerada por',
      hideBelow: 'md',
      render: (documento) => documento.generated_label || '--',
    },
    {
      key: 'data',
      header: 'Geracao',
      align: 'right',
      hideBelow: 'lg',
      render: (documento) => formatDate(documento.generated_at),
    },
    {
      key: 'assinaturas',
      header: 'Assinaturas',
      render: (documento) => {
        if (!documento.signature_flow) {
          return <span className="text-2xs text-ink-500">Sem coleta</span>;
        }
        const estado = situacaoDaColeta[documento.signature_flow.status] ?? {
          label: documento.signature_flow.status,
          tone: 'neutral' as const,
        };
        return <Badge tone={estado.tone}>{estado.label}</Badge>;
      },
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (documento) => {
        const estado = rotuloDaSituacao[documento.status] ?? {
          label: documento.status,
          tone: 'neutral' as const,
        };
        return <Badge tone={estado.tone}>{estado.label}</Badge>;
      },
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (documento) => (
        <div className="flex justify-end gap-1">
          <AcaoComIcone
            acao="verTexto"
            rotulo="Ver texto"
            onClick={() => setVendo(documento)}
          />
          <BaixarDocumento documento={documento} />
          <LinkComIcone
            acao="abrir"
            rotulo="Ver a operacao"
            to={`/operacoes/${documento.operation_id}`}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={titulo} description={descricao} />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
          <Select
            label="Situacao"
            className="h-9"
            options={situacoes}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(documento) => documento.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon={kind === 'ccg' ? 'stamp' : 'file'}
              title={`Nenhuma ${titulo.toLowerCase()} gerada`}
              description="A geracao acontece na operacao, onde o texto ganha sentido. Abra a operacao e gere por la."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {vendo ? (
        <Modal
          open
          onClose={() => setVendo(null)}
          title={`${titulo} ${vendo.number}`}
          description={`Texto gerado a partir da versao ${vendo.template_version} do template.`}
          size="lg"
          footer={<Button onClick={() => setVendo(null)}>Fechar</Button>}
        >
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded bg-ink-50 p-4 text-sm text-ink-800">
            {vendo.body}
          </pre>
        </Modal>
      ) : null}
    </div>
  );
}

export function DraftsPage() {
  return (
    <GeneratedListPage
      kind="minuta"
      titulo="Minutas"
      descricao="Documentos contratuais gerados a partir dos templates."
    />
  );
}

export function CounterGuaranteesPage() {
  return (
    <GeneratedListPage
      kind="ccg"
      titulo="CCG"
      descricao="Cartas de contragarantia geradas a partir dos templates."
    />
  );
}

/**
 * Coletas de assinatura.
 *
 * Nenhum provedor de assinatura eletronica esta integrado. A plataforma registra
 * a assinatura colhida fora dela, com quem afirmou e quando, e o aviso na tela diz
 * isso em vez de deixar a pessoa supor.
 */
export function SignaturesPage() {
  // Abas (P-MIN-4): os pedidos ao provedor de assinatura, a coleta interna (registro de
  // assinatura colhida fora) e a configuracao da empresa.
  const { can } = useAuth();
  const vePedidos = can(PERMISSAO_VER_PEDIDOS);
  const veConfiguracao = vePedidos || can(PERMISSAO_CONFIGURAR_ASSINATURA);
  const [aba, setAba] = useState<'pedidos' | 'coletas' | 'configuracao'>(
    vePedidos ? 'pedidos' : 'coletas',
  );
  const abas = [
    ...(vePedidos
      ? [{ chave: 'pedidos' as const, rotulo: 'Pedidos ao provedor' }]
      : []),
    { chave: 'coletas' as const, rotulo: 'Coletas internas' },
    ...(veConfiguracao
      ? [{ chave: 'configuracao' as const, rotulo: 'Configuracao' }]
      : []),
  ];
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [aberto, setAberto] = useState<SignatureFlow | null>(null);

  const consulta = useQuery({
    queryKey: ['signature-flows', { status, offset }],
    queryFn: ({ signal }) => signatures.list({ status, limit: limite, offset }, signal),
  });

  const contagem = consulta.data?.byStatus ?? {};

  const colunas: Column<SignatureFlow>[] = [
    {
      key: 'numero',
      header: 'Coleta',
      render: (coleta) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{coleta.number}</p>
          <p className="truncate text-2xs text-ink-500">
            {coleta.notes || 'Sem observacoes'}
          </p>
        </div>
      ),
    },
    {
      key: 'origem',
      header: 'Aberta por',
      hideBelow: 'md',
      render: (coleta) => coleta.opened_label || '--',
    },
    {
      key: 'abertura',
      header: 'Abertura',
      align: 'right',
      hideBelow: 'lg',
      render: (coleta) => formatDate(coleta.created_at),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (coleta) => {
        const estado = situacaoDaColeta[coleta.status] ?? {
          label: coleta.status,
          tone: 'neutral' as const,
        };
        return <Badge tone={estado.tone}>{estado.label}</Badge>;
      },
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (coleta) => (
        <AcaoComIcone acao="abrir" rotulo="Abrir" onClick={() => setAberto(coleta)} />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Assinaturas"
        description="Assinatura eletronica pelo provedor da empresa e registro da assinatura colhida fora da plataforma."
      />

      {abas.length > 1 ? (
        <div role="tablist" aria-label="Abas de assinaturas" className={trilhoDeAbas}>
          {abas.map((item) => (
            <button
              key={item.chave}
              type="button"
              role="tab"
              aria-selected={aba === item.chave}
              onClick={() => setAba(item.chave)}
              className={
                aba === item.chave ? classeDaAba(true, 'xs') : classeDaAba(false, 'xs')
              }
            >
              {item.rotulo}
            </button>
          ))}
        </div>
      ) : null}

      {aba === 'pedidos' ? <SignatureRequestsTab /> : null}
      {aba === 'configuracao' ? <SignatureSettingsTab /> : null}

      {aba === 'coletas' ? (
        <>
          <Alert tone="info" title="Coleta interna">
            Aqui a plataforma registra a assinatura colhida fora dela, guardando quem
            afirmou e quando. A assinatura eletronica pelo provedor fica na aba Pedidos
            ao provedor.
          </Alert>

          {consulta.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar as coletas">
              {consulta.error.message}
            </Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <KpiCard
              label="Em coleta"
              value={String(contagem.em_coleta ?? 0)}
              icon="signature"
              tone="warning"
              loading={consulta.isPending}
            />
            <KpiCard
              label="Concluidas"
              value={String(contagem.concluido ?? 0)}
              icon="check"
              tone="success"
              loading={consulta.isPending}
            />
            <KpiCard
              label="Canceladas"
              value={String(contagem.cancelado ?? 0)}
              icon="close"
              tone="danger"
              loading={consulta.isPending}
            />
          </div>

          <Card>
            <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
              <Select
                label="Situacao"
                className="h-9"
                options={[
                  { value: '', label: 'Todas as situacoes' },
                  { value: 'em_coleta', label: 'Em coleta' },
                  { value: 'concluido', label: 'Concluida' },
                  { value: 'cancelado', label: 'Cancelada' },
                ]}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setOffset(0);
                }}
              />
            </div>

            <DataTable
              columns={colunas}
              items={consulta.data?.items ?? []}
              keyOf={(coleta) => coleta.id}
              loading={consulta.isPending}
              empty={
                <EmptyState
                  icon="signature"
                  title="Nenhuma coleta aberta"
                  description="A coleta nasce sobre a minuta ou a CCG da operacao. Gere o documento e abra a coleta por la."
                />
              }
            />

            <Pagination
              total={consulta.data?.total ?? 0}
              limit={limite}
              offset={offset}
              onChange={setOffset}
            />
          </Card>

          {aberto ? (
            <Modal
              open
              onClose={() => setAberto(null)}
              title={`Coleta ${aberto.number}`}
              description="Registro de assinatura colhida fora da plataforma."
              size="md"
              footer={<Button onClick={() => setAberto(null)}>Fechar</Button>}
            >
              <SignatureFlowBlock flowId={aberto.id} />
            </Modal>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
